import {
  Alert,
  AlertOverviewEntry,
  AlertPriority,
  AlertPriorityOrder,
  AlertStatus,
  AlertStreamControlMessage,
  AlertStreamMessage,
  AlertV2Entry,
  WatcherrApiNotFoundError,
  WebSocketService,
} from '@ixicare/resources';
import { State } from '../types';
import SoundService from '~/services/SoundService';
import uniq from 'lodash/uniq';
import { AlertReceivedMethodSource, SimpleAlert } from './Alerts.types';
import { getAlertMethod, transformIntoSimpleAlert } from './Alert.utils';
import { action, computed, makeAutoObservable, runInAction } from 'mobx';
import AlertStore from './Alert';
import PersistentStoreService from '~services/PersistentStoreService';
import { backoff } from '~utils/promises';
import NotificationService from '~services/NotificationService';
import { shouldPlayAlertSound } from '~config/soundNotification';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { notifySessionExpired } from '~services/sessionExpiry';

const RECEIVED_ALERT_UUIDS_KEY = 'receivedAlertUuids';
const RECEIVED_ALERT_UUIDS_LIMIT = 100;
const NO_SEG_HANDLING_KEY = 'ixicare.alertModeNoSegHandling';

const isControl = (entry: AlertStreamMessage): entry is AlertStreamControlMessage =>
  'type' in entry && !('uuid' in entry);

// Bucket key for single-site users (currentSiteUuid === undefined).
const NO_SITE_KEY = '__no_site__';

// Per-site lists of simple alerts + per-site fetch flags. The detailed
// `alertStoreMap` is shared across sites (uuids are globally unique).
class SiteAlertState {
  activeAlerts: SimpleAlert[] = [];
  inactiveAlerts: SimpleAlert[] = [];
  rejectedAlerts: SimpleAlert[] = [];
  lastUpdate: number = 0;
  isFetchingApi: boolean = false;
  isFetchingLatestApi: boolean = false;
  didFailRefresh: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }
}

/*
Receives alerts from multiple sources (e.g. api calls, websockets, notifications)
Keeps a record of which alerts are still active per site.
Switching sites is a constant-time bucket swap — previously-visited sites stay
warm in memory. WS pushes are routed to the right bucket by `alert.siteUuid`,
so the primary site (still the only one the current server-side WS feeds) keeps
filling in the background while the user browses elsewhere.
Controls the SoundService for the site the user is on duty for (their HCP group's
site), falling back to the currently-viewed site when they aren't on duty
anywhere — so an observer browsing another site stays audible for the alarms
they're still responsible for.
*/
class AlertsStore {
  private rootStore: State;

  // Per-site buckets. Lazy-created via stateFor().
  private states = new Map<string, SiteAlertState>();

  // Detail alert stores indexed by uuid. Shared across sites — same alert is
  // never in two buckets and uuids are globally unique. AlertDetailPage looks
  // alerts up here regardless of selected site.
  public alertStoreMap = new Map<string, AlertStore>();

  // The site the UI is currently focused on. Undefined for single-site users
  // (their alerts live in the NO_SITE_KEY bucket).
  private currentSiteUuid: string | undefined = undefined;

  // The site this user is on duty for (their HCP group's site), when that isn't
  // simply the viewed site. Sound and notification counters follow this rather
  // than the viewed site: an observer browsing another site is still on the hook
  // for their group's alarms and must stay audible for them.
  private dutySiteUuid: string | undefined = undefined;

  private interval?: any;

  private socket: WebSocketService<AlertStreamMessage[]> | null = null;
  public isSocketConnected: boolean = false;
  public isSocketConnecting: boolean = true;

  static readonly INTERNAL_SYSTEM_RESOLVE_REASON = 'Resolving duplicate alert message';

  constructor(rootStore: State) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rejectAlerts: action.bound,
      fetchAlerts: action.bound,
      isDisconnected: computed,
      // Must NOT become actions: actions run untracked, so observers reading
      // these during render (the alert-mode pill and its count) would never
      // re-render when the underlying state changes.
      openCountFor: false,
      isNoSegHandling: false,
    });
  }

  private stateFor(siteUuid: string | undefined): SiteAlertState {
    const key = siteUuid ?? NO_SITE_KEY;
    let s = this.states.get(key);
    if (!s) {
      s = new SiteAlertState();
      this.states.set(key, s);
    }
    return s;
  }

  private get current(): SiteAlertState {
    return this.stateFor(this.currentSiteUuid);
  }

  // Bucket that drives sound + notification counters: the on-duty site when set,
  // otherwise the viewed site (HCPs, and observers who aren't on duty anywhere).
  private get alerting(): SiteAlertState {
    return this.stateFor(this.dutySiteUuid ?? this.currentSiteUuid);
  }

  // Set (or clear with `undefined`) the site this user is on duty for. Re-fires
  // sounds/counters so they immediately reflect the new duty bucket.
  setDutySite = (siteUuid: string | undefined) => {
    if (this.dutySiteUuid === siteUuid) return;
    this.dutySiteUuid = siteUuid;
    this.informExternals();
  };

  // Sites with no HCP groups where this user opted into handling alarms. There is
  // no group to join on an unsegmented site, so activation is a local preference:
  // it only unmutes and unlocks the actions on the observer feed they already
  // receive — no routing changes. Cleared on logout with the persisted store.
  private noSegHandlingSites: string[] = PersistentStoreService.get<string[]>(NO_SEG_HANDLING_KEY) ?? [];

  isNoSegHandling = (siteUuid: string | undefined): boolean => !!siteUuid && this.noSegHandlingSites.includes(siteUuid);

  /** The unsegmented site this user opted into handling, independent of what
   *  they're currently viewing — duty is durable, so it must not be derived
   *  from the site on screen. Duty is single-site, so the first entry is it. */
  get noSegDutySite(): string | undefined {
    return this.noSegHandlingSites[0];
  }

  setNoSegHandling = (siteUuid: string, handling: boolean) => {
    // Replaces rather than appends: duty is single-site, and accumulating
    // entries would make `noSegDutySite` return whichever happened to be first.
    this.noSegHandlingSites = handling ? [siteUuid] : this.noSegHandlingSites.filter((s) => s !== siteUuid);
    PersistentStoreService.set(NO_SEG_HANDLING_KEY, this.noSegHandlingSites);
    this.informExternals();
  };

  // Open alarms still awaiting a response in a site's bucket. Rejected ones are
  // excluded, matching what drives the alarm sound. Never creates a bucket — a
  // site that was never fetched simply reads as 0.
  openCountFor = (siteUuid: string | undefined): number => {
    const state = this.states.get(siteUuid ?? NO_SITE_KEY);
    if (!state) return 0;
    return state.activeAlerts.filter((a) => !state.rejectedAlerts.some((r) => r.uuid === a.uuid)).length;
  };

  private get knownAlerts() {
    return this.current.activeAlerts.concat(this.current.inactiveAlerts);
  }

  private isInactiveIn(state: SiteAlertState, alert: SimpleAlert) {
    return state.inactiveAlerts.some((a) => alert.uuid === a.uuid);
  }

  private isActiveIn(state: SiteAlertState, alert: SimpleAlert) {
    return state.activeAlerts.some((a) => alert.uuid === a.uuid);
  }

  private isRejectedIn(state: SiteAlertState, alert: SimpleAlert) {
    return state.rejectedAlerts.some((a) => alert.uuid === a.uuid);
  }

  // Apply a batch of alerts to a specific site bucket. Returns whether the
  // bucket was modified so callers can decide whether to re-fire sounds.
  private applyKnownAlerts = (state: SiteAlertState, alerts: AlertOverviewEntry[] | AlertV2Entry[]): boolean => {
    if (!this.rootStore.auth.isLoggedIn) return false;

    const simpleAlerts = transformIntoSimpleAlert(alerts);
    let isModified = false;
    for (const alert of simpleAlerts) {
      if (alert.status === AlertStatus.Open) {
        if (this.isInactiveIn(state, alert)) continue;
        if (this.isActiveIn(state, alert)) continue;
        isModified = true;
        state.activeAlerts.push(alert);
      } else {
        if (this.isInactiveIn(state, alert)) continue;
        isModified = true;
        state.activeAlerts = state.activeAlerts.filter((a) => a.uuid !== alert.uuid);
        state.inactiveAlerts.push(alert);
      }

      this.alertStoreMap.get(alert.uuid)?.setStatus(alert.status);
    }

    return isModified;
  };

  private removeAlertFromState(uuid: string) {
    // A uuid lives in at most one bucket, so stop after the one that holds it
    // instead of reallocating every visited site's lists.
    for (const state of this.states.values()) {
      const inState =
        state.activeAlerts.some((x) => x.uuid === uuid) ||
        state.inactiveAlerts.some((x) => x.uuid === uuid) ||
        state.rejectedAlerts.some((x) => x.uuid === uuid);
      if (!inState) continue;
      state.activeAlerts = state.activeAlerts.filter((x) => x.uuid !== uuid);
      state.inactiveAlerts = state.inactiveAlerts.filter((x) => x.uuid !== uuid);
      state.rejectedAlerts = state.rejectedAlerts.filter((x) => x.uuid !== uuid);
      break;
    }
    this.alertStoreMap.delete(uuid);
    this.informExternals();
  }

  // Sounds + notification counters follow the on-duty site (falling back to the
  // viewed site) — you stay audible for the alarms you're responsible for.
  private informExternals() {
    this.updateSounds();
    NotificationService.updateAlertCounters(this.alerting.activeAlerts);
  }

  // Keep a record of the last 100 received alerts
  // to prevent duplicate acknowledgements
  private processAcknowledgements = async (simpleAlerts: SimpleAlert[], source: AlertReceivedMethodSource) => {
    const uuids = (await PersistentStoreService.get<string[]>(RECEIVED_ALERT_UUIDS_KEY)) ?? [];
    const diff = simpleAlerts.filter((x) => !uuids.includes(x.uuid));
    if (diff.length) {
      const updatedUuids = uuids.concat(diff.map((x) => x.uuid)).slice(-RECEIVED_ALERT_UUIDS_LIMIT);
      await PersistentStoreService.set(RECEIVED_ALERT_UUIDS_KEY, updatedUuids);

      await Promise.all(diff.map((alert) => this.acknowledgeAlert(alert, source)));
    }
  };

  private acknowledgeAlert = async (alert: SimpleAlert, source: AlertReceivedMethodSource) => {
    const methodKey = getAlertMethod(source);
    try {
      await backoff(
        (signal) =>
          Alert.createAlertAcknowledgement(
            {
              alertUuid: alert.uuid,
              method: methodKey,
            },
            signal
          ),
        { maxRetries: 15 }
      );
    } catch (err) {
      console.error('Alert.createAlertAcknowledgement', err);
    }
  };

  rejectAlerts(alerts: AlertStore[]) {
    const simpleAlerts = transformIntoSimpleAlert(alerts);
    let isModified = false;

    for (const alert of simpleAlerts) {
      // Find which site bucket owns this alert; rejection sticks to that site
      // even if the user is currently viewing another one.
      let target: SiteAlertState | undefined;
      for (const state of this.states.values()) {
        if (this.isActiveIn(state, alert)) {
          target = state;
          break;
        }
      }
      if (!target) continue;
      if (this.isRejectedIn(target, alert)) continue;
      console.info('AlertsStore', 'reject', alert.uuid);
      target.rejectedAlerts.push(alert);
      isModified = true;
    }

    if (isModified) {
      this.informExternals();
    }
  }

  private updateSounds() {
    try {
      const active = this.alerting.activeAlerts;
      const rejected = this.alerting.rejectedAlerts;
      const activeTypes = active
        .filter((a) => !rejected.some((b) => a.uuid === b.uuid))
        .filter((a) => shouldPlayAlertSound(a.priority))
        .map((a) => a.priority);
      SoundService.updateActiveSoundTypes(uniq(activeTypes));
    } catch (err) {
      console.error(err);
    }
  }

  get alertsSorted() {
    return this.knownAlerts
      .map((sa) => this.alertStoreMap.get(sa.uuid))
      .filter((a): a is AlertStore => !!a)
      .sort((a, b) => b.created.getTime() - a.created.getTime());
  }

  get openAlerts() {
    return this.alertsSorted.filter((a) => a.isOpen);
  }

  get cancelledAlerts() {
    return this.alertsSorted.filter((a) => a.isCancelled);
  }

  get acceptedAlerts() {
    return this.alertsSorted.filter((a) => a.isAcceptedOwnExcluded);
  }

  get isDisconnected() {
    return !this.isSocketConnected && Date.now() - this.current.lastUpdate > 40e3;
  }

  // The app received a new active alert (via PUSH) but hasn't been able to fetch it from the API yet
  get isInLimbo() {
    return this.current.activeAlerts.some((x) => !this.alertStoreMap.has(x.uuid));
  }

  get isLoading() {
    return this.current.isFetchingApi || this.isSocketConnecting || this.isInLimbo;
  }

  get groupedAlerts(): {
    [key in AlertPriority]: { items: AlertStore[]; open: number };
  } {
    const grouped = AlertPriorityOrder.reduce<any>((result, prio) => {
      const alerts = this.alertsSorted.filter((alert) => alert.priority === prio);
      result[prio] = {
        items: alerts,
        open: alerts.filter((a) => a.isOpen).length,
      };
      return result;
    }, {});
    return grouped;
  }

  fetchAlerts = async (siteUuid?: string, maxRetries = 3) => {
    const siteChanged = siteUuid !== this.currentSiteUuid;
    if (siteChanged) {
      this.currentSiteUuid = siteUuid;
      // New site is now active — refresh sounds/counters from its bucket.
      this.informExternals();
    }

    await this.loadAlertsInto(siteUuid, maxRetries);
  };

  // Fill another site's bucket (the on-duty site while browsing elsewhere)
  // without changing which site the UI is focused on. The websocket keeps that
  // bucket live afterwards; this seeds what was already open before we connected.
  fetchAlertsForSite = async (siteUuid: string | undefined, maxRetries = 3) => {
    if (siteUuid === this.currentSiteUuid) return;
    await this.loadAlertsInto(siteUuid, maxRetries);
  };

  private loadAlertsInto = async (siteUuid: string | undefined, maxRetries: number) => {
    const state = this.stateFor(siteUuid);
    if (state.isFetchingApi) {
      return;
    }

    this.startListening();
    state.isFetchingApi = true;

    try {
      const alerts = await backoff(() => Alert.getAlertsV2({ limit: 25, site_uuid: siteUuid }), { maxRetries });

      runInAction(() => {
        state.didFailRefresh = false;
        state.isFetchingApi = false;
        this.updateAlertOverviewStoreMap(state, alerts);
        const modified = this.applyKnownAlerts(state, alerts);

        // Background fill for active alerts not in the response. Bound to the
        // same `state` we just fetched, so a site switch during fetch can't
        // pollute the wrong bucket.
        const missingActiveAlerts = state.activeAlerts.filter((x) => !alerts.some((y) => x.uuid === y.uuid));
        if (missingActiveAlerts.length) {
          console.info('Alerts', 'missing active alerts', missingActiveAlerts.length);
        }
        missingActiveAlerts.map((a) => this.fetchAlert(a.uuid));

        if (modified && state === this.alerting) this.informExternals();
      });
    } catch (error) {
      runInAction(() => {
        state.isFetchingApi = false;
        state.didFailRefresh = true;
      });
      NotificationService.disconnected();
      console.error('alerts api error', error);
    }
  };

  fetchLatestAlerts = async ({
    numberOfDaysAgo = 1,
    maxRetries = 3,
    forceFetch = false,
    siteUuid,
  }: { numberOfDaysAgo?: number; maxRetries?: number; forceFetch?: boolean; siteUuid?: string } = {}) => {
    const state = this.stateFor(siteUuid);
    if (!forceFetch && (state.isFetchingLatestApi || state.lastUpdate > Date.now() - 15e3)) {
      return;
    }

    this.startListening();
    state.isFetchingLatestApi = true;

    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, numberOfDaysAgo));

    try {
      const alertEvents = await backoff(
        (signal) =>
          Alert.getAlertsHistoryV2(
            { start_date: start.toISOString(), end_date: end.toISOString(), site_uuid: siteUuid },
            signal
          ),
        { maxRetries }
      );

      const alerts: AlertOverviewEntry[] = alertEvents.reduce((acc: AlertOverviewEntry[], cur) => {
        const alert_id = cur.uuid;

        if (cur.resolvedBy && cur.resolvedReason === AlertsStore.INTERNAL_SYSTEM_RESOLVE_REASON) {
          return acc;
        }

        if (!acc.some((a) => a.uuid === alert_id)) {
          acc.push(cur);
        }

        return acc;
      }, []);

      runInAction(() => {
        state.didFailRefresh = false;
        state.isFetchingLatestApi = false;
        this.updateAlertOverviewStoreMap(state, alerts);
        const modified = this.applyKnownAlerts(state, alerts);

        const missingActiveAlerts = state.activeAlerts.filter((x) => !alerts.some((y) => x.uuid === y.uuid));
        if (missingActiveAlerts.length) {
          console.info('Alerts', 'missing active alerts', missingActiveAlerts.length);
        }
        missingActiveAlerts.map((a) => this.fetchAlert(a.uuid));

        if (modified && state === this.alerting) this.informExternals();
      });
    } catch (error) {
      runInAction(() => {
        state.isFetchingLatestApi = false;
        state.didFailRefresh = true;
      });
      NotificationService.disconnected();
      console.error('alerts api error', error);
    }
  };

  fetchAlert = (uuid: string) => {
    backoff((signal) => Alert.getAlertV2(uuid, signal))
      .then((alert) => {
        runInAction(() => {
          const state = this.stateFor(alert.siteUuid ?? undefined);
          this.updateAlertDetailStoreMap(state, [alert]);
          const modified = this.applyKnownAlerts(state, [alert]);
          if (modified && state === this.alerting) this.informExternals();
        });
      })
      .catch((e) => {
        if (e instanceof WatcherrApiNotFoundError) {
          // GHOST alert
          // It was created at one point, but now it's gone
          console.warn(`Removing ghost alert`, uuid);
          runInAction(() => {
            this.removeAlertFromState(uuid);
          });
        } else {
          console.info(`alert error api`, e);
        }
      });
  };

  private updateAlertDetailStoreMap(state: SiteAlertState, alerts: AlertV2Entry[]) {
    for (const alert of alerts) {
      const existingAlertStore = this.alertStoreMap.get(alert.uuid);
      if (existingAlertStore) {
        existingAlertStore.setDetailData(alert);
      } else {
        this.alertStoreMap.set(alert.uuid, new AlertStore(this.rootStore, alert));
      }
    }
    state.lastUpdate = Date.now();
  }

  private updateAlertOverviewStoreMap(state: SiteAlertState, alerts: AlertOverviewEntry[]) {
    for (const alert of alerts) {
      const existingAlertStore = this.alertStoreMap.get(alert.uuid);
      if (existingAlertStore) {
        existingAlertStore.setOverviewData(alert);
      } else {
        this.alertStoreMap.set(alert.uuid, new AlertStore(this.rootStore, undefined, alert));
      }
    }
    state.lastUpdate = Date.now();
  }

  private startListening() {
    if (!this.socket && this.rootStore.auth.isLoggedIn) {
      runInAction(() => {
        this.isSocketConnected = false;
        this.isSocketConnecting = true;
      });
      console.info(`Start listening to alerts`);

      this.socket = Alert.listenOnNewAlerts(this.handleWsNewAlerts);

      this.socket.onClose = this.handleWsClose;

      this.socket.onOpen = this.handleWsOpen;

      this.socket.open();
    }
    if (!this.interval) {
      this.interval = setInterval(() => {
        void this.fetchLatestAlerts({ numberOfDaysAgo: 1, siteUuid: this.currentSiteUuid });
        // Keep the on-duty bucket fresh too — it backs the "open elsewhere"
        // count and the alarm sound while the user browses another site.
        if (this.dutySiteUuid !== undefined && this.dutySiteUuid !== this.currentSiteUuid) {
          void this.fetchLatestAlerts({ numberOfDaysAgo: 1, siteUuid: this.dutySiteUuid });
        }
      }, 3 * 60e3);
    }
  }

  private stopListening = () => {
    console.info(`close listening to alerts`);
    if (this.socket) {
      this.socket.forceDisconnect();
      this.socket = null;
    }
  };

  private handleWsNewAlerts = (data: AlertStreamMessage[]) => {
    runInAction(() => {
      this.isSocketConnected = true;
    });

    // /alarms-streaming multiplexes alerts with control messages. Route
    // presence updates to PresenceStore; pass alert payloads through.
    for (const entry of data) {
      if (isControl(entry) && entry.type === 'self_presence_changed') {
        this.rootStore.presence.onSelfPresenceChanged();
      } else if (isControl(entry) && entry.type === 'self_hcp_group_changed') {
        void this.handleSelfHcpGroupChanged();
      } else if (isControl(entry) && entry.type === 'session_revoked') {
        // An admin forced this account out. The socket stays up because it
        // authenticated at connect time, so this arrives even though the token is
        // already gone — without it the user sits on a dead session until their
        // next request 401s.
        notifySessionExpired();
      }
    }
    const alerts = data.filter((e): e is AlertV2Entry => !isControl(e));
    if (alerts.length === 0) return;

    // Route each alert to its own site bucket by `siteUuid`. Backend pushes
    // arrive on two channels: the HCP-routed channel (segmentation-driven,
    // for plain HCPs) and per-site observer channels (multi-site users only).
    // Same alert can arrive twice — applyKnownAlerts dedupes by uuid.
    const bySite = new Map<string | undefined, AlertV2Entry[]>();
    for (const alert of alerts) {
      const key = alert.siteUuid ?? undefined;
      const bucket = bySite.get(key);
      if (bucket) bucket.push(alert);
      else bySite.set(key, [alert]);
    }
    let anyTouchedCurrent = false;
    for (const [siteUuid, batch] of bySite) {
      const state = this.stateFor(siteUuid);
      this.updateAlertDetailStoreMap(state, batch);
      const modified = this.applyKnownAlerts(state, batch);
      if (modified && state === this.alerting) anyTouchedCurrent = true;
    }
    if (anyTouchedCurrent) this.informExternals();
  };

  // Another session (or an admin) changed this user's HCP group. Re-fetch the
  // profile; if the group actually changed for this session, clear + reconnect +
  // refetch so routing and the visible list re-resolve under the new membership.
  // The session that made the change already updated, so its group is unchanged
  // here and it skips the churn.
  private handleSelfHcpGroupChanged = async () => {
    const auth = this.rootStore.auth;
    const userId = auth.user?.id;
    if (userId === undefined) return;
    const before = auth.user?.hcpGroupId ?? null;
    await auth.fetchUser(userId);
    const after = auth.user?.hcpGroupId ?? null;
    if (before === after) return;
    this.clearSite(this.currentSiteUuid);
    this.reconnect();
    void this.fetchAlerts(this.currentSiteUuid);
  };

  private handleWsOpen = () => {
    console.info('websocket opened');
    runInAction(() => {
      this.isSocketConnected = true;
      this.isSocketConnecting = false;
    });
  };

  private handleWsClose = () => {
    console.info('websocket closed in foreground');

    runInAction(() => {
      this.isSocketConnected = false;
      this.isSocketConnecting = false;
    });
  };

  // Drop a single site's bucket (and its shared detail stores). Used when an
  // installer flips between passive (all-site observer feed) and active
  // (group-scoped feed) for a site: the alerts delivered under the old mode
  // must not linger, so we clear then refetch under the new feed.
  clearSite = (siteUuid: string | undefined) => {
    const key = siteUuid ?? NO_SITE_KEY;
    const state = this.states.get(key);
    if (state) {
      for (const alert of [...state.activeAlerts, ...state.inactiveAlerts, ...state.rejectedAlerts]) {
        this.alertStoreMap.delete(alert.uuid);
      }
      this.states.set(key, new SiteAlertState());
    }
    if (key === (this.currentSiteUuid ?? NO_SITE_KEY)) {
      this.informExternals();
    }
  };

  // Tear down and re-open the alarm socket so the backend re-resolves which
  // channels this connection observes (it snapshots them at connect time from
  // the profile's RECEIVE links). Call after a group (un)assignment commits.
  reconnect = () => {
    this.stopListening();
    this.startListening();
  };

  reset = () => {
    this.stopListening();
    this.states.clear();
    this.alertStoreMap.clear();
    this.isSocketConnected = false;
    this.isSocketConnecting = true;
    this.currentSiteUuid = undefined;
    this.dutySiteUuid = undefined;
    // Must be cleared here too: reset() runs on logout before the persisted
    // copy is wiped, and without this the next user in the same tab inherits
    // the previous one's opt-in and is silently treated as on duty.
    this.noSegHandlingSites = [];
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  };
}

export default AlertsStore;
